import React, { useState } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';

const WellnessAnalytics = () => {
  const [activeChart, setActiveChart] = useState('bookings');

  // Mock data for charts
  const bookingData = [
    { month: 'Jan', bookings: 28, revenue: 4200 },
    { month: 'Feb', bookings: 35, revenue: 5250 },
    { month: 'Mar', bookings: 42, revenue: 6300 },
    { month: 'Apr', bookings: 38, revenue: 5700 },
    { month: 'May', bookings: 45, revenue: 6750 },
    { month: 'Jun', bookings: 52, revenue: 7800 }
  ];

  const wellnessOutcomes = [
    { outcome: 'Stress Relief', percentage: 89, color: '#2563EB' },
    { outcome: 'Energy Boost', percentage: 84, color: '#0EA5E9' },
    { outcome: 'Better Sleep', percentage: 78, color: '#059669' },
    { outcome: 'Pain Relief', percentage: 73, color: '#D97706' },
    { outcome: 'Mental Clarity', percentage: 81, color: '#7C3AED' }
  ];

  const serviceDistribution = [
    { name: 'EES Sessions', value: 45, color: '#2563EB' },
    { name: 'Day Passes', value: 25, color: '#0EA5E9' },
    { name: 'Weekly Packages', value: 20, color: '#059669' },
    { name: 'Monthly Plans', value: 10, color: '#D97706' }
  ];

  const guestSatisfaction = [
    { month: 'Jan', rating: 4.2, reviews: 18 },
    { month: 'Feb', rating: 4.4, reviews: 22 },
    { month: 'Mar', rating: 4.6, reviews: 28 },
    { month: 'Apr', rating: 4.5, reviews: 25 },
    { month: 'May', rating: 4.7, reviews: 32 },
    { month: 'Jun', rating: 4.8, reviews: 35 }
  ];

  const chartOptions = [
    { id: 'bookings', label: 'Booking Trends', icon: 'TrendingUp' },
    { id: 'outcomes', label: 'Wellness Outcomes', icon: 'Heart' },
    { id: 'services', label: 'Service Distribution', icon: 'PieChart' },
    { id: 'satisfaction', label: 'Guest Satisfaction', icon: 'Star' }
  ];

  const renderChart = () => {
    switch (activeChart) {
      case 'bookings':
        return (
          <div>
            <h3 className="text-lg font-medium text-text-primary mb-4">Booking & Revenue Trends</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={bookingData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" stroke="#64748B" />
                <YAxis yAxisId="left" stroke="#64748B" />
                <YAxis yAxisId="right" orientation="right" stroke="#64748B" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#FFFFFF', 
                    border: '1px solid #E2E8F0', 
                    borderRadius: '8px' 
                  }} 
                />
                <Line 
                  yAxisId="left" 
                  type="monotone" 
                  dataKey="bookings" 
                  stroke="#2563EB" 
                  strokeWidth={3}
                  dot={{ fill: '#2563EB', strokeWidth: 2 }}
                  name="Bookings"
                />
                <Line 
                  yAxisId="right" 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="#0EA5E9" 
                  strokeWidth={3}
                  dot={{ fill: '#0EA5E9', strokeWidth: 2 }}
                  name="Revenue ($)"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        );

      case 'outcomes':
        return (
          <div>
            <h3 className="text-lg font-medium text-text-primary mb-4">Wellness Outcome Success Rates</h3>
            <div className="space-y-4">
              {wellnessOutcomes.map((outcome, index) => (
                <div key={index} className="flex items-center space-x-4">
                  <div className="w-32 text-sm text-text-secondary">{outcome.outcome}</div>
                  <div className="flex-1 bg-secondary-200 rounded-full h-3">
                    <div
                      className="h-3 rounded-full transition-all duration-500"
                      style={{
                        width: `${outcome.percentage}%`,
                        backgroundColor: outcome.color
                      }}
                    ></div>
                  </div>
                  <div className="w-12 text-sm font-medium text-text-primary text-right">
                    {outcome.percentage}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'services':
        return (
          <div>
            <h3 className="text-lg font-medium text-text-primary mb-4">Service Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={serviceDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={120}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {serviceDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => [`${value}%`, 'Percentage']}
                  contentStyle={{ 
                    backgroundColor: '#FFFFFF', 
                    border: '1px solid #E2E8F0', 
                    borderRadius: '8px' 
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-2 gap-4 mt-4">
              {serviceDistribution.map((service, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: service.color }}
                  ></div>
                  <span className="text-sm text-text-secondary">{service.name}</span>
                  <span className="text-sm font-medium text-text-primary">{service.value}%</span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'satisfaction':
        return (
          <div>
            <h3 className="text-lg font-medium text-text-primary mb-4">Guest Satisfaction & Reviews</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={guestSatisfaction}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" stroke="#64748B" />
                <YAxis yAxisId="left" stroke="#64748B" domain={[0, 5]} />
                <YAxis yAxisId="right" orientation="right" stroke="#64748B" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#FFFFFF', 
                    border: '1px solid #E2E8F0', 
                    borderRadius: '8px' 
                  }} 
                />
                <Bar 
                  yAxisId="left" 
                  dataKey="rating" 
                  fill="#059669" 
                  radius={[4, 4, 0, 0]}
                  name="Average Rating"
                />
                <Line 
                  yAxisId="right" 
                  type="monotone" 
                  dataKey="reviews" 
                  stroke="#2563EB" 
                  strokeWidth={3}
                  name="Review Count"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-surface rounded-lg shadow-elevation-1 p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <h2 className="text-xl font-semibold text-text-primary mb-4 sm:mb-0">
          Wellness Analytics
        </h2>
        
        {/* Chart Navigation */}
        <div className="flex flex-wrap gap-2">
          {chartOptions.map((option) => (
            <button
              key={option.id}
              onClick={() => setActiveChart(option.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                activeChart === option.id
                  ? 'bg-primary text-white' :'bg-secondary-100 text-text-secondary hover:bg-secondary-200 hover:text-text-primary'
              }`}
            >
              <Icon name={option.icon} size={16} />
              <span className="hidden sm:inline">{option.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="min-h-[350px]">
        {renderChart()}
      </div>

      {/* Quick Insights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 pt-6 border-t border-border">
        <div className="text-center">
          <div className="text-2xl font-bold text-primary mb-1">94%</div>
          <div className="text-sm text-text-secondary">Overall Success Rate</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-success mb-1">4.8</div>
          <div className="text-sm text-text-secondary">Average Rating</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-accent-600 mb-1">73%</div>
          <div className="text-sm text-text-secondary">Return Guest Rate</div>
        </div>
      </div>
    </div>
  );
};

export default WellnessAnalytics;